import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';

import { Vec3 } from '../../domain';
import { CurrentLocation } from '../../services';
import { IViewport } from '../../viewports';
import { Sphere3DActor } from '../actors/sphere-3d-actor';
import { CrosshairOptions, CrosshairOverlay } from '../overlay-actors/crosshair-overlay';
import { CurrentLocationCprOverlay } from '../overlay-actors/current-location-cpr-overlay';
import {
    CurrentLocationDisplayProperties,
    DisplayDataFor,
    DisplayFeatureType,
    IDisplayFeature,
    IDisplayObject,
    ViewMode,
} from '../types';

// ============================================================================
// Strategy interface
// ============================================================================

interface ICurrentLocationRenderer {
    initialize(viewport: IViewport, featureId: string): void;
    applyProperties(
        data: DisplayDataFor<DisplayFeatureType.CURRENT_LOCATION>,
        properties: CurrentLocationDisplayProperties
    ): void;
    dispose(): void;
    getActors(): vtkActor[];
}

/**
 * Extract actual centerline node position from CurrentLocation
 * worldPosition may be slightly different than actual centerline node
 */
function extractCenterlinePosition(data: CurrentLocation): Vec3 {
    if (!data.centerlinePosition) return data.worldPosition;

    const nodeIndex = data.centerlinePosition.nodeIndex;
    return data.centerlinePosition.centerline.getNodeAt(nodeIndex).point;
}

// ============================================================================
// 3D strategy – Sphere actor on the VTK renderer
// ============================================================================

class CurrentLocationRenderer3D implements ICurrentLocationRenderer {
    private renderer: vtkRenderer | null = null;
    private actor: Sphere3DActor | null = null;

    initialize(viewport: IViewport, _featureId: string): void {
        this.actor = new Sphere3DActor();
        this.renderer = viewport.getRenderer(1);
        this.renderer.addActor(this.actor.actor);
    }

    applyProperties(
        data: DisplayDataFor<DisplayFeatureType.CURRENT_LOCATION>,
        properties: CurrentLocationDisplayProperties
    ): void {
        if (!this.actor) return;

        const position = data ? extractCenterlinePosition(data) : ([0, 0, 0] as Vec3);
        const opacity = data ? (properties.opacity ?? 1.0) : 0.0;

        this.actor.setPosition(position);
        this.actor.setRadius(properties.sphere.radius);
        this.actor.setColor(properties.sphere.color);
        this.actor.setOpacity(opacity);
    }

    dispose(): void {
        if (this.renderer && this.actor) {
            this.renderer.removeActor(this.actor.actor);
            this.actor.dispose();
        }
        this.renderer = null;
        this.actor = null;
    }

    getActors(): vtkActor[] {
        return this.actor ? [this.actor.actor] : [];
    }
}

// ============================================================================
// 2D strategy – Crosshair on the overlay layer
// ============================================================================

class CurrentLocationRenderer2D implements ICurrentLocationRenderer {
    private viewport: IViewport | null = null;
    private crosshair: CrosshairOverlay | null = null;

    initialize(viewport: IViewport, featureId: string): void {
        this.viewport = viewport;

        const options: CrosshairOptions = {
            id: `current-location-crosshair-${featureId}`,
            style: {
                color: '#00FF00',
                lineWidth: 1,
                gap: 20,
                size: 80,
                centerDotRadius: 2,
            },
        };

        this.crosshair = new CrosshairOverlay(options);
        viewport.getOverlayLayer().addOverlayObject(this.crosshair);
    }

    applyProperties(
        data: DisplayDataFor<DisplayFeatureType.CURRENT_LOCATION>,
        properties: CurrentLocationDisplayProperties
    ): void {
        if (!this.crosshair) return;

        if (!data) {
            this.crosshair.visible = false;
            return;
        }

        const position = data.worldPosition;
        this.crosshair.visible = true;
        this.crosshair.setWorldPosition(position);
        this.crosshair.updateStyle({ color: properties.crosshair.color });
    }

    dispose(): void {
        if (this.viewport && this.crosshair) {
            this.viewport.getOverlayLayer().removeOverlayObject(this.crosshair.id);
        }
        this.crosshair = null;
        this.viewport = null;
    }

    getActors(): vtkActor[] {
        return [];
    }
}

// ============================================================================
// CPR strategy – Horizontal line on the overlay layer
// ============================================================================

class CurrentLocationRendererCPR implements ICurrentLocationRenderer {
    private viewport: IViewport | null = null;
    private overlay: CurrentLocationCprOverlay | null = null;

    initialize(viewport: IViewport, featureId: string): void {
        this.viewport = viewport;

        this.overlay = new CurrentLocationCprOverlay({
            id: `current-location-cpr-${featureId}`,
            color: '#ffffff',
        });
        viewport.getOverlayLayer().addOverlayObject(this.overlay);
    }

    applyProperties(
        data: DisplayDataFor<DisplayFeatureType.CURRENT_LOCATION>,
        _properties: CurrentLocationDisplayProperties
    ): void {
        if (!this.overlay) return;

        if (!data?.centerlinePosition) {
            this.overlay.hide();
            return;
        }

        const { centerline, nodeIndex } = data.centerlinePosition;
        if (centerline.totalLength <= 0) {
            this.overlay.hide();
            return;
        }

        const normalizedY = centerline.getDistanceAt(nodeIndex) / centerline.totalLength;
        this.overlay.updatePosition(normalizedY);
    }

    dispose(): void {
        if (this.viewport && this.overlay) {
            this.viewport.getOverlayLayer().removeOverlayObject(this.overlay.id);
        }
        this.overlay = null;
        this.viewport = null;
    }

    getActors(): vtkActor[] {
        return [];
    }
}

// ============================================================================
// Unified DisplayObject
// ============================================================================

function createRenderer(viewMode: ViewMode): ICurrentLocationRenderer {
    switch (viewMode) {
        case ViewMode.VIEW_3D:
            return new CurrentLocationRenderer3D();
        case ViewMode.VIEW_CPR_STRETCHED:
        case ViewMode.VIEW_CPR_STRAIGHTENED:
            return new CurrentLocationRendererCPR();
        default:
            return new CurrentLocationRenderer2D();
    }
}

export class CurrentLocationDisplayObject implements IDisplayObject {
    readonly feature: IDisplayFeature<DisplayFeatureType.CURRENT_LOCATION>;
    readonly viewMode: ViewMode;

    private renderer: ICurrentLocationRenderer;

    constructor(feature: IDisplayFeature<DisplayFeatureType.CURRENT_LOCATION>, viewMode: ViewMode) {
        this.feature = feature;
        this.viewMode = viewMode;
        this.renderer = createRenderer(viewMode);
    }

    getActors(): vtkActor[] {
        return this.renderer.getActors();
    }

    initialize(viewport: IViewport): void {
        this.renderer.initialize(viewport, this.feature.id);
        this.applyProperties();
    }

    update(): void {
        this.applyProperties();
    }

    dispose(): void {
        this.renderer.dispose();
    }

    private applyProperties(): void {
        this.renderer.applyProperties(this.feature.data, this.feature.properties);
    }
}
